import { IntervalTypeEnum, Price, Pricebook, PricebookBundle } from '../models/PricebookData';
import { SelectionData } from '../api/apis/PricebookApi';
import { formatDecimalOrWholeDollars } from './utilityFunctions';

export const derivePricebookSelections = (
  options: PricebookBundle[],
  selectionIds: SelectionData[],
): PricebookBundle[] => {
  const selectedIds = new Set(selectionIds.map(s => s.pricebookId));
  return options.filter(bundle => selectedIds.has(bundle.pricebook.id));
};

export const hasAvailablePackages = (
  available: PricebookBundle[],
  selected: PricebookBundle[] | undefined,
): boolean => {
  const selectedIds = new Set(selected?.map(pb => pb.pricebook.id) ?? []);
  return available.some(pb => !selectedIds.has(pb.pricebook.id));
};

export const isFreemiumPricebook = (pricebook: Pricebook): boolean =>
  Boolean(pricebook.metadata?.freemium) && !pricebook.addOn;

export function getActivePrice(
  prices: Price[],
  options: { preferOneTime?: boolean } = {},
): Price | undefined {
  const match = options.preferOneTime
    ? prices.find(p => p.intervalType === IntervalTypeEnum.None)
    : prices.find(p => p.intervalType !== IntervalTypeEnum.None);
  return match || prices[0];
}

export function getIntervalLabel(price: Price | undefined): string {
  if (!price?.price) return '';
  if (price.intervalType === IntervalTypeEnum.Month) return '/mo';
  if (price.intervalType === IntervalTypeEnum.Year) return '/yr';
  return '';
}

export function getAutoRenewLabel(price: Price | undefined): string {
  if (price?.intervalType === IntervalTypeEnum.Month) return 'auto-renews monthly';
  if (price?.intervalType === IntervalTypeEnum.Year) return 'auto-renews annually';
  return 'one-time payment';
}

export function getPricebookPopupBody(description: string | undefined): string {
  return (description?.trim() ?? '').replace(/([^.!?])$/, '$1.');
}

export function getPricebookPopupTitle(
  metadata: { subHeader?: string; nonProfit?: boolean } | null | undefined,
  priceInDollars: number,
): string {
  const subHeader = metadata?.subHeader;
  if (subHeader) return subHeader;
  if (metadata?.nonProfit) {
    return priceInDollars === 0 ? 'For Non-Profits' : 'Special Promo Offer!*';
  }
  return '';
}

export function getPriceAmountLabel(price: Price | undefined): string {
  return formatDecimalOrWholeDollars((price?.price ?? 0) / 100);
}

export function getPriceWithIntervalLabel(price: Price | undefined): string {
  if (!price) return '';
  return `$${getPriceAmountLabel(price)}${getIntervalLabel(price)}`;
}

export function getPricebookListPriceLabel(price: Price | undefined): string {
  const interval = !price?.price
    ? 'No cost'
    : price.intervalType === IntervalTypeEnum.Month
      ? 'Auto-renews monthly'
      : price.intervalType === IntervalTypeEnum.Year
        ? 'Auto-renews annually'
        : 'One-time payment';
  return `$${getPriceAmountLabel(price)} | ${interval}`;
}
